import React, { useEffect, useState } from "react";
import CustomInputText from "./CustomInputText";
import MDButton from "./MDButton";
import CustomImageUploader from "./CustomImageUploader";
import MDBox from "./MDBox";
import MDInput from "./MDInput";
import { AddPinCall, DeletePinCall, EditPinCall } from "../redux/api/title.api";
import { translatePinCall } from "../redux/api/pinTranslation.api";
import { basicURL } from "../apiManager/APIConfig";
import { store } from "../redux/store";
import axios from "axios";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import TranslateRoundedIcon from "@mui/icons-material/TranslateRounded";
import { openAppAlert, openAppConfirm } from "./AppDialogs";
import TranslateConfirmDialog from "./TranslateConfirmDialog";
import AITextAssistantButton from "./AITextAssistantButton";

const CustomDrawer = ({ fetchSlide, lang, slide_id, data, open, toggleDrawer }) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState(data?.files?.[0]?.url);
  const [showLoader, setShowLoader] = useState(false);
  /* AI translation: opens the reusable confirm dialog. Only available
     for an existing (persisted) pin — there's nothing to translate
     before the row exists in the DB. */
  const [translateOpen, setTranslateOpen] = useState(false);
  /* Detect whether the form has been edited since the pin was loaded.
     We block translation while dirty so we don't translate a stale
     source — admin should save first. */
  const isDirty =
    String(title || "") !== String(data?.title || "") ||
    String(description || "") !== String(data?.body || "");
  const auth = store.getState().auth || {};
  const authId = auth?.id || auth?.user_id || null;
  const role = auth?.role;
  const isPrivilegedEditor =
    role === "prof" || role === "professor" || role === "university_admin" || role === "admin";

  const isExisting = !!data?.id;
  const isOwner =
    !!data?.owner_id &&
    !!authId &&
    String(data.owner_id).trim() === String(authId).trim();
  const canEdit = !isExisting || isOwner || isPrivilegedEditor;

  useEffect(() => {
    // const extractedTitle = data?.pinLocalizations?.filter((e) => e.field === "title");
    // const extractedBody = data?.pinLocalizations?.filter((e) => e.field === "body");
    setTitle(data?.title);
    setDescription(data?.body);
  }, [data]);

  const onChangeTitle = (e) => {
    setTitle(e.target.value); // Update password state
  };

  const onChangeDescription = (e) => {
    setDescription(e.target.value); // Update password state
  };
  const canUploadFiles =
    data?.id && role !== "prof" && role !== "professor" && role !== "university_admin";

  const handlePhotoMaterial = async (file) => {
    setImage(file);

    if (file) {
      try {
        setShowLoader(true);
        // Handle `.zip` file upload
        const formdata = new FormData();
        formdata.append("file", file);
        formdata.append("entity_type", "pin");
        formdata.append("entity_id", data?.id);
        formdata.append("destination", "pins");
        formdata.append("folder_name", "pins");

        const access_token = store.getState().auth?.access_token;
        let myHeaders = new Headers();
        myHeaders.append("Authorization", access_token);

        myHeaders = {
          Authorization: access_token,
        };

        const response = await axios.post(`${basicURL}/file/upload`, formdata, {
          headers: myHeaders,
          onUploadProgress: (progressEvent) => {
            // Calculate the progress percentage
            const percent = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            // setUploadPercentage(percent);
          },
        });
        setShowLoader(false);
        // setImage(`${basicURL}/${response?.data?.extracted_files?.[1]?.path}`);

        console.log(
          "ZIP file uploaded:",
          `${basicURL}/${response?.data?.extracted_files?.[1]?.path}/{z}/{x}/{y}.png`
        );
        await fetchSlide();
      } catch (error) {
        console.error("Upload error:", error);
      } finally {
        setShowLoader(false);
      }
    }
  };

  const save = async () => {
    if (!canEdit) {
      await openAppAlert({
        title: "Permission denied",
        message: "You can only edit your own private pins.",
      });
      return;
    }

    if (data?.id) {
      const body = {
        id: data?.id,
        title,
        lang,
        type: "his",
        slide_id,
        body: description,
        locations: JSON.stringify({ data }),
      };
      await EditPinCall(body);
    } else {
      const body = {
        title,
        lang,
        type: "his",
        slide_id,
        body: description,
        locations: JSON.stringify({ data }),
      };
      await AddPinCall(body);
    } //fetch
    await fetchSlide();
    toggleDrawer(false, false);
  };

  const deletePin = async () => {
    if (!canEdit) {
      await openAppAlert({
        title: "Permission denied",
        message: "You can only delete your own private pins.",
      });
      return;
    }
    const pin_id = data?.id;
    const confirmation = await openAppConfirm({
      title: "Delete pin?",
      message:
        "Are you sure you want to delete this pin? All uploaded footage for this pin will be removed permanently.",
      confirmText: "Delete",
      cancelText: "Cancel",
      confirmColor: "error",
    });
    if (confirmation) {
      const res = await DeletePinCall(pin_id);
      if (res?.status === 200) {
        await fetchSlide();
        toggleDrawer(false, false);
      } else {
        await openAppAlert({
          title: "Delete failed",
          message: res?.message || "Pin delete failed.",
        });
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => toggleDrawer(false, !isExisting)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {data?.id ? "Edit pin information" : "Add pin information"}
      </DialogTitle>
      <DialogContent dividers>
        <MDBox component="form" role="form" sx={{ mt: 1 }}>
          <MDBox mb={2}>
            <MDInput
              value={title}
              onChange={onChangeTitle}
              type="text"
              label="Title"
              fullWidth
            />
          </MDBox>
          <MDBox mb={2}>
            <MDBox
              mb={1}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={1}
            >
              <Typography variant="caption" color="text.secondary">
                Description
              </Typography>
              {canEdit && (
                <AITextAssistantButton
                  value={description}
                  onApply={setDescription}
                  context="pin"
                  label="AI description"
                />
              )}
            </MDBox>
            <MDInput
              onChange={onChangeDescription}
              multiline
              rows={6}
              value={description}
              type="text"
              label="Description"
              fullWidth
            />
          </MDBox>
        </MDBox>
        {data?.id && (
          <CustomImageUploader
            value={image}
            files={data?.files}
            setFile={handlePhotoMaterial}
            setImage={setImage}
            canUpload={canUploadFiles}
          />
        )}
        {!data?.id && (
          <MDBox mt={1} sx={{ fontSize: 13, color: "text.secondary" }}>
            First save the pin title and description. After that you can attach images to it.
          </MDBox>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <MDButton
          disabled={!description && !title}
          color="info"
          variant="contained"
          onClick={save}
        >
          Save
        </MDButton>
        <MDButton
          disabled={!data?.id}
          color="error"
          variant="outlined"
          onClick={deletePin}
        >
          Delete
        </MDButton>
        {/* Only show Translate for an existing pin that has at least
            some source text and isn't currently being edited. The
            backend will overwrite any prior translation rows. */}
        {data?.id && canEdit && (
          <Tooltip
            title={
              isDirty
                ? "Save your changes first, then translate."
                : !title && !description
                ? "Add a title or description, save, then translate."
                : "Translate this pin into all supported app languages (overwrites existing)."
            }
          >
            <span>
              <MDButton
                variant="outlined"
                color="info"
                disabled={isDirty || (!title && !description)}
                onClick={() => setTranslateOpen(true)}
                startIcon={<TranslateRoundedIcon />}
              >
                Translate
              </MDButton>
            </span>
          </Tooltip>
        )}
        <MDButton
          variant="outlined"
          color="secondary"
          onClick={() => toggleDrawer(false, !isExisting)}
        >
          Cancel
        </MDButton>
      </DialogActions>
      <TranslateConfirmDialog
        open={translateOpen}
        onClose={() => setTranslateOpen(false)}
        title="Translate this pin"
        description={
          <>
            The pin{" "}
            <strong>{title || data?.title || "(untitled)"}</strong> — title
            and description — will be translated into every supported app
            language using AI.
          </>
        }
        targetLangsLabel="32 app languages: English, Portuguese (PT/BR), Spanish, Italian, German, Dutch, Chinese (Simplified/Traditional), Japanese, Korean, Arabic, French, Russian, Turkish, Polish, Indonesian, Vietnamese, Hindi, Persian, Romanian, Greek, Czech, Hungarian, Swedish, Danish, Norwegian, Finnish, Malay, Tagalog, Kazakh, Turkmen — source locale is skipped"
        itemSummaryLabel="1 pin"
        confirmLabel="Translate to all languages"
        onConfirm={async ({ setStatus }) => {
          setStatus("Translating pin into every locale…");
          return translatePinCall(data?.id);
        }}
        summaryRenderer={(res) => (
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              {res?.created ?? 0} created · {res?.updated ?? 0} updated.
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              {(res?.targetLangs || []).map((code) => (
                <Chip
                  key={code}
                  size="small"
                  color="success"
                  variant="outlined"
                  label={code.toUpperCase()}
                  sx={{ mb: 0.5 }}
                />
              ))}
            </Stack>
          </Stack>
        )}
      />
    </Dialog>
  );
};

export default CustomDrawer;
